import { NotificationStatus } from '@/__generated__/graphql';
import { useDispatch } from '@/store';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import UPDATE_NOTIFICATION_STATUS from '@features/notifications/graphql/mutations/updateNotificationStatus';
import GET_NOTIFICATIONS from '@features/notifications/graphql/queries/getNotifications';
import { getEntityAttributes } from '@features/notifications/utils/entitiesHandlers';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Cached, NotificationsOff } from '@mui/icons-material';
import { Box, Button, List, Paper, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { Suspense, useCallback, useState, useTransition, type CSSProperties, type FC } from 'react';
import { FixedSizeList } from 'react-window-1.8.10';
import { MenuListItem } from './MenuListItem';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import GET_UNREAD_NOTIFICATIONS_COUNT from '@features/notifications/graphql/queries/getUnreadNotificationsCount';

interface Props {
  onClose: () => void;
}

const statusGroups = {
  unread: [NotificationStatus.New, NotificationStatus.Unread],
  read: [NotificationStatus.Read],
};

const STYLE = {
  MENU_WIDTH: 360,
  MENU_HEIGHT: 540,
  MENU_ITEM_HEIGHT: 58,
} as const;

const LIMIT = 10;

const SuspenseComponent: FC<Props & { selectedTab: 'unread' | 'read' }> = ({
  onClose,
  selectedTab,
}) => {
  const { i18n } = useLingui();
  const dispatch = useDispatch();
  const [pending, startTransition] = useTransition();
  const currentStatuses = statusGroups[selectedTab];
  const { data, fetchMore } = useSuspenseQuery(GET_NOTIFICATIONS, {
    variables: {
      filter: {
        statuses: currentStatuses,
      },
      pagination: {
        limit: LIMIT,
      },
    },
  });
  const [updateNotificationStatusMutate] = useMutation(UPDATE_NOTIFICATION_STATUS);

  const updateNotificationStatus = useCallback(
    async (id: number, status: NotificationStatus) => {
      await updateNotificationStatusMutate({
        refetchQueries: [GET_NOTIFICATIONS, GET_UNREAD_NOTIFICATIONS_COUNT],
        variables: {
          input: {
            id,
            status,
          },
        },
        optimisticResponse: {
          updateNotificationStatus: {
            __typename: 'UpdateNotificationStatusPayload',
            notification: {
              __typename: 'Notification',
              id,
              status,
            },
          },
        },
      });
    },
    [updateNotificationStatusMutate],
  );

  const handleDismissNotification = useCallback(
    (notificationId: number) => {
      updateNotificationStatus(notificationId, NotificationStatus.Dismissed);
    },
    [updateNotificationStatus],
  );

  const handleViewNotification = useCallback(
    async (notificationId: number) => {
      const notification = data.notificationsConnection.items.find(
        ({ id }) => id === notificationId,
      );
      if (!notification || !notification.notificationObject) return;
      const entity = notification.notificationObject.entity;
      const { helperIcon } = getEntityAttributes(entity, dispatch, i18n);
      if (helperIcon?.action) helperIcon.action();
      updateNotificationStatus(notificationId, NotificationStatus.Read);
      onClose();
    },
    [updateNotificationStatus, data.notificationsConnection, dispatch, i18n, onClose],
  );

  const loadMoreNotifications = useCallback(() => {
    const hasMore = data.notificationsConnection.pageInfo.nextCursor != null;
    if (!hasMore) return;
    startTransition(() => {
      fetchMore({
        variables: {
          pagination: {
            limit: LIMIT,
            cursor: data.notificationsConnection.pageInfo.nextCursor,
          },
        },
        updateQuery(prev, { fetchMoreResult }) {
          return {
            ...prev,
            notificationsConnection: {
              ...prev.notificationsConnection,
              pageInfo: fetchMoreResult.notificationsConnection.pageInfo,
              items: [
                ...prev.notificationsConnection.items,
                ...fetchMoreResult.notificationsConnection.items,
              ],
            },
          };
        },
      });
    });
  }, [fetchMore, data.notificationsConnection.pageInfo]);

  // useEffect(() => {
  //   const unsubCreated = subscribeToMore({ document: NOTIFICATION_CREATED });
  //   const unsubDeleted = subscribeToMore({ document: NOTIFICATION_DELETED });
  //   return () => {
  //     unsubCreated();
  //     unsubDeleted();
  //   };
  // }, [subscribeToMore]);

  const hasMore = data.notificationsConnection.pageInfo.nextCursor != null;

  return (
    <>
      <List
        disablePadding
        sx={{
          width: STYLE.MENU_WIDTH,
          paddingBottom: 1,
        }}
      >
        {!data.notificationsConnection.items.length ? (
          <Box height={STYLE.MENU_HEIGHT} display="grid" sx={{ placeItems: 'center', pb: 14 }}>
            <NoNotifications />
          </Box>
        ) : (
          <FixedSizeList
            className="custom-scrollbar"
            height={STYLE.MENU_HEIGHT}
            width="100%"
            itemSize={STYLE.MENU_ITEM_HEIGHT}
            itemCount={data.notificationsConnection.items.length + Number(hasMore)}
            itemKey={(index: number) => data.notificationsConnection.items[index]?.id ?? null}
          >
            {({ style, index }: { style: CSSProperties; index: number }) => {
              if (index === data.notificationsConnection.items.length) {
                return (
                  <Box style={style} pt={1} textAlign="center">
                    <Button onClick={loadMoreNotifications} disabled={pending} endIcon={<Cached />}>
                      <Trans>Cargar más</Trans>
                    </Button>
                  </Box>
                );
              }

              const notification = data.notificationsConnection.items[index];
              return (
                <Box
                  style={style}
                  borderBottom={
                    data.notificationsConnection.items.length - 1 === index ? undefined : 1
                  }
                  pb="1px"
                  borderColor="divider"
                >
                  <MenuListItem
                    notification={notification}
                    handleDismissNotification={handleDismissNotification}
                    handleViewNotification={handleViewNotification}
                  />
                </Box>
              );
            }}
          </FixedSizeList>
        )}
      </List>
    </>
  );
};

const NoNotifications = () => (
  <Box
    px={2}
    pt={2}
    display="flex"
    flexDirection="column"
    alignItems="center"
    gap={2}
    color="text.secondary"
  >
    <NotificationsOff fontSize="large" />
    <Typography variant="body1" fontStyle="italic">
      <Trans>No hay notificaciones nuevas</Trans>
    </Typography>
  </Box>
);

export const MenuList: FC<Props> = (props) => {
  const { _ } = useLingui();
  const [selectedTab, setSelectedTab] = useState<'unread' | 'read'>('unread');

  return (
    <Paper sx={{ border: 1, borderColor: 'divider', maxWidth: STYLE.MENU_WIDTH + 10 }}>
      <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)} sx={{ mb: 1 }}>
        <Tab value="unread" label={_(msg`Nuevos`)} />
        <Tab value="read" label={_(msg`Leídos`)} />
      </Tabs>
      <ApolloErrorBoundary>
        <Suspense
          fallback={
            <List
              disablePadding
              sx={{
                width: STYLE.MENU_WIDTH,
                paddingBottom: 1,
                height: STYLE.MENU_HEIGHT,
                overflow: 'hidden',
              }}
            >
              {Array.from({ length: 25 }).map((_, index, { length }) => (
                <Box
                  key={index}
                  borderBottom={length - 1 === index ? undefined : 1}
                  pb="1px"
                  borderColor="divider"
                >
                  <Skeleton variant="rectangular" height={STYLE.MENU_HEIGHT - 1} />
                </Box>
              ))}
            </List>
          }
        >
          <SuspenseComponent {...props} selectedTab={selectedTab} />
        </Suspense>
      </ApolloErrorBoundary>
    </Paper>
  );
};
